import React, {useEffect, useRef, useState} from "react";
import {Alert, Card, CardBody, CardHeader} from "reactstrap";
import NTMLoadingModal from "../../components/Modal/NTMLoadingModal";
import {ErrorDialog, SuccessDialog, ConfirmDialog} from "@nextome/messages";
import NTMForm from "@nextome/nxtform";
import NTMButton from "../../components/Buttons";
import {Client} from "../../redux/utils";
import {SETTINGS} from "../../redux/utils/Client";

let client = new Client();
const TEST_EMAIL_ENDPOINT = "tests/testInvioEmail";
const EMAIL_TEST_COOLDOWN_SECONDS = 10;

function getRetryAfterSeconds(response) {
	const data = response.data;
	if (data && Number.isInteger(data.retryAfterSeconds) && data.retryAfterSeconds >= 0) {
		return data.retryAfterSeconds;
	}

	const headers = response.headers || {};
	const retryAfterHeader = headers["retry-after"] || headers["Retry-After"];
	if (typeof retryAfterHeader === "string" && /^\d+$/.test(retryAfterHeader.trim())) {
		return Number(retryAfterHeader);
	}

	return null;
}

function getRequestErrorMessage(error) {
	if (error.response) {
		const data = error.response.data;
		if (data && typeof data.message === "string") return data.message;
		if (data && typeof data.error === "string") return data.error;
		if (typeof data === "string") return data;
	}
	return error.message || "Errore durante il test di invio email.";
}

export default function SettingsPage() {

	let [settings, setSettings] = useState({});
	let [modal, setModal] = useState(null);
	let [emailTestResult, setEmailTestResult] = useState(null);
	let [isTestingEmail, setIsTestingEmail] = useState(false);
	let [emailTestCooldownUntil, setEmailTestCooldownUntil] = useState(0);
	let [emailTestCooldownSeconds, setEmailTestCooldownSeconds] = useState(0);
	let emailTestCooldownUntilRef = useRef(0);

	let manageError = e => {
		let error;
		if (e.response) {
			error = e.response.data.message;
		} else {
			error = e.message;
		}
		setModal(
			<ErrorDialog title={"Errore"} message={error} onClose={closeModal}/>
		);
	};

	const getSettings = () => client.get(SETTINGS)
		.then(response => {
			setSettings(response.data);
			setModal(null);
		})
		.catch(manageError);

	useEffect(() => {
		setModal(<NTMLoadingModal/>);
		getSettings();
	}, []);

	const closeModal = () => setModal(null);

	useEffect(() => {
		if (!emailTestCooldownUntil) return undefined;

		const updateCooldown = () => {
			const remainingSeconds = Math.max(0, Math.ceil((emailTestCooldownUntil - Date.now()) / 1000));
			setEmailTestCooldownSeconds(remainingSeconds);

			if (remainingSeconds === 0 && emailTestCooldownUntilRef.current === emailTestCooldownUntil) {
				emailTestCooldownUntilRef.current = 0;
				setEmailTestCooldownUntil(0);
			}
		};

		updateCooldown();
		const interval = setInterval(updateCooldown, 250);
		return () => clearInterval(interval);
	}, [emailTestCooldownUntil]);

	const extendEmailTestCooldown = seconds => {
		const deadline = Math.max(
			emailTestCooldownUntilRef.current,
			Date.now() + seconds * 1000
		);
		emailTestCooldownUntilRef.current = deadline;
		setEmailTestCooldownUntil(deadline);
		setEmailTestCooldownSeconds(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
	};

	const testEmail = () => {
		if (isTestingEmail || emailTestCooldownUntilRef.current > Date.now()) return;

		extendEmailTestCooldown(EMAIL_TEST_COOLDOWN_SECONDS);
		setIsTestingEmail(true);
		setEmailTestResult({
			color: "info",
			message: "Invio di prova in corso..."
		});

		client.save(undefined, TEST_EMAIL_ENDPOINT)
			.then(response => {
				if (response.status === 200) {
					setEmailTestResult({
						color: "success",
						message: "Email di prova inviata correttamente."
					});
					return;
				}

				setEmailTestResult({
					color: "warning",
					message: `Risposta inattesa dal server (HTTP ${response.status}).`
				});
			})
			.catch(error => {
				const status = error.response && error.response.status;

				if (status === 401 || status === 403) {
					setEmailTestResult({
						color: "danger",
						message: "Sessione non valida o utente non autorizzato."
					});
					return;
				}

				if (status === 429) {
					const retryAfterSeconds = getRetryAfterSeconds(error.response);
					if (retryAfterSeconds === null) {
						setEmailTestResult({
							color: "warning",
							message: "Richiesta limitata dal server, ma il tempo residuo non è stato indicato."
						});
						return;
					}

					extendEmailTestCooldown(retryAfterSeconds);
					setEmailTestResult({
						color: "warning",
						message: `Troppi tentativi. Il server indica un tempo residuo di ${retryAfterSeconds} secondi.`,
						showRetryCountdown: true
					});
					return;
				}

				setEmailTestResult({
					color: "danger",
					message: getRequestErrorMessage(error)
				});
			})
			.then(() => setIsTestingEmail(false));
	};

	const onSubmit = (values) => {
		setModal(
			<ConfirmDialog
				message={`Vuoi salvare le modifiche?`}
				onCancel={closeModal}
				onConfirm={() => {
					console.log(values);
					setModal(<NTMLoadingModal/>);
					client.update(values, SETTINGS)
						.then(() => {
							setModal(
								<SuccessDialog
									title={"Operazione eseguita"}
									onClose={closeModal}
								/>
							);
							getSettings();
						})
						.catch(manageError);
				}}
			/>
		);
	};

	if (!settings) return <div/>;
	let initialValues = Object.assign({}, settings);

	for (let key in initialValues) {
		if (initialValues.hasOwnProperty(key)) {
			if (RegExp('^\\d+$').test(initialValues[key])) {
				initialValues[key] = Number.parseInt(initialValues[key]);
			}
		}
	}

	return (
		<div className="animated fadeIn" style={{padding: "1rem"}}>
			{modal}
			<Card className="mb-3">
				<CardHeader>Impostazioni</CardHeader>
				<CardBody>
					<NTMForm
						initialValues={initialValues}
						onSubmit={onSubmit}
						cols={2}
						saveButton={"top"}
						formData={[
					{
						type: "number",
						name: "ca",
						label: "Commissione APPAcutis (ca) iva esclusa",
						prepend: "€",
						props: {
							step: 0.01
						}
					},
					{
						type: "number",
						name: "cp",
						label: "Commissione Partner (cp) iva esclusa",
						prepend: "€",
						props: {
							step: 0.01
						}
					},
					{
						type: "number",
						name: "qss",
						label: "Quota Spese Sistema (qss) sotto i 10€ iva inclusa",
						prepend: "€",
						props: {
							step: 0.01
						}
					},
					{
						type: "number",
						name: "nexiFee",
						label: "Fee percentuale di Nexi",
						prepend: "%",
						props: {
							step: 0.01
						}
					},
					{
						type: "number",
						name: "qscr",
						label: "Quota Spese Calcolo Ripartizione Prodotto (qscr)",
						props: {
							disabled: true
						},
						prepend: "€"
					},
					{
						type: "number",
						name: "itVat",
						label: "IVA",
						prepend: "%"
					},
					{
						type: "number",
						name: "fdsos",
						label: "Percentuale Fondo Sostegno",
						prepend: "%"
					},
					{
						type: "number",
						name: "fdg",
						label: "Percentuale Fondo di Garanzia (fdg)",
						prepend: "%"
					},
					{
						type: "number",
						name: "fdsol",
						label: "Percentuale Fondo di Solidarietà (fdsol)",
						props: {
							disabled: true
						},
						prepend: "%"
					},
					{
						type: "number",
						name: "tucumB",
						label: "Credito Beneficiario (in Tucum)"
					},
					{
						type: "number",
						name: "coffeePrice",
						label: "Prezzo per Offri un caffè",
						prepend: "€",
						props: {
							step: 0.1
						}
					},
					{
						type: "number",
						name: "startdl",
						label: "Prezzo di partenza per Donazione Libera",
						prepend: "€",
						props: {
							step: 0.1
						}
					},
					{
						type: "number",
						name: "lmdl",
						label: "Limite Minimo Donazione Libera",
						prepend: "€",
						props: {
							step: 0.1
						}
					},
					{
						type: "number",
						name: "lmaxdl",
						label: "Limite Massimo Donazione Libera",
						prepend: "€",
						props: {
							step: 0.1
						}
					},
					{
						type: "number",
						name: "externalFundMinDonation",
						label: "Limite Minimo Donazione Fondo esterno",
						prepend: "€",
						props: {
							step: 0.1
						}
					},
					{
						type: "number",
						name: "externalFundMaxDonation",
						label: "Limite Massimo Donazione Fondo Esterno",
						prepend: "€",
						props: {
							step: 0.1
						}
					},
					{
						type: "number",
						name: "externalFundTucumFee",
						label: "Fee per progetto Tucum in Donazione Fondo Esterno fino 10€",
						prepend: "€",
						props: {
							step: 0.1
						}
					},
					{
						type: "number",
						name: "thresholdFee",
						label: "Soglia dopo la quale si incrementa il qss",
						prepend: "€",
						props: {
							step: 0.01
						}
					},
					{
						type: "number",
						name: "sf",
						label: "Soglia Massima Fatturabile (iva inclusa)",
						prepend: "€"
					},
					{
						type: "number",
						name: "nfg",
						label: "Limite Fatture Giornaliero"
					},
					{
						type: "number",
						name: "maxProductPrice",
						label: "Limite Massimo prezzo cliente",
						prepend: "€",
					},
					{
						type: "number",
						name: "minDiscount",
						label: "Percentuale minima di sconto del partner",
						prepend: "%"
					},
					{
						type: "number",
						name: "card",
						label: "Prezzo card Beneficiario (iva esclusa)",
						prepend: "€",
						props: {
							step: 0.00001
						}
					},
					{
						type: "number",
						name: "mntPayCard",
						label: "N° mesi che il beneficiario può pagare in anticipo",
						props: {
							min: 0,
							max: 5
						}
					},
					{
						type: "textarea",
						name: "welcome",
						label: "Frase di benvenuto",
						props: {rows: 3}
					},
					{
						type: "textarea",
						name: "tnks",
						label: "Frase di ringraziamento",
						props: {rows: 3}
					},
					{
						type: "url",
						name: "privacyLink",
						label: "URL pagina \"Informativa Privacy\""
					},
					{
						type: "url",
						name: "termsLink",
						label: "URL pagina \"Termini e Condizioni\""
					},

					{
						type: "url",
						name: "aboutLink",
						label: "URL pagina \"About\""
					},
					{
						type: "url",
						name: "projectsLink",
						label: "URL pagina \"Progetti\""
					},
					{
						type: "url",
						name: "coffeeInfoLink",
						label: "URL pagina \"Offri un caffè\""
					},
					{
						type: "url",
						name: "discoverLink",
						label: "URL pagina \"Scopri di più\""
					},
					{
						type: "url",
						name: "tucumInfoLink",
						label: "URL pagina \"Progetto Tucum\" pagina donazione"
					},
						]}
					/>
				</CardBody>
			</Card>
			<Card>
				<CardHeader>Test funzionalità</CardHeader>
				<CardBody>
					<NTMButton
						primary
						type="button"
						onClick={testEmail}
						disabled={isTestingEmail || emailTestCooldownSeconds > 0}
					>
						Prova funzionamento invio email
					</NTMButton>
					{emailTestResult &&
						<Alert color={emailTestResult.color} className="mt-3" role="status">
							{emailTestResult.message}
							{emailTestResult.showRetryCountdown && emailTestCooldownSeconds > 0 &&
								` Il pulsante sarà nuovamente disponibile tra ${emailTestCooldownSeconds} secondi.`}
						</Alert>
					}
				</CardBody>
			</Card>
		</div>
	);
}
